import Tree from '../entities/Tree.js';
import Rock from '../entities/Rock.js';
import Cactus from '../entities/Cactus.js';
import Pyramid from '../entities/Pyramid.js';
import DryBush from '../entities/DryBush.js';
import Tumbleweed from '../entities/Tumbleweed.js';
import { ENVIRONMENT, PYRAMIDS } from '../constants/gameSettings.js';

export default class EnvironmentManager {
    constructor(scene, physics, game = null) {
        this.scene = scene;
        this.physics = physics;
        this.game = game;
        
        // Store environmental elements
        this.trees = [];
        this.rocks = [];
        this.cacti = [];
        this.pyramids = [];
        this.dryBushes = [];
        this.tumbleweeds = [];
        this.obstacles = [];
        this.roadExclusionZones = [];
        
        // Initialize scene userData for tracking obstacles
        if (!this.scene.userData) this.scene.userData = {};
        this.scene.userData.obstacles = this.obstacles;
        
        this.initialized = false;
    }
    
    addEnvironmentalElements() {
        // Add trees and rocks to the scene
        const terrainSize = ENVIRONMENT.TERRAIN_SIZE; // Size of environment area
        
        // Reset obstacles list for physics
        this.obstacles = [];
        this.scene.userData.obstacles = this.obstacles;
        
        // Reset exclusion zones
        this.roadExclusionZones = [];
        
        // Add impressive rock formations first
        this.addRockFormations(terrainSize, this.roadExclusionZones);
        
        // Add trees in clusters
        this.addTreeClusters(terrainSize, this.roadExclusionZones);
        
        // Add individual trees with mixed styles
        this.addIndividualTrees(terrainSize, this.roadExclusionZones);
        
        // Add clusters of cacti
        this.addCactiClusters(terrainSize, this.roadExclusionZones);
        
        // Add individual cacti
        this.addIndividualCacti(terrainSize, this.roadExclusionZones);
        
        // Add clusters of rocks
        this.addRockClusters(terrainSize, this.roadExclusionZones);
        
        // Add individual rocks
        this.addIndividualRocks(terrainSize, this.roadExclusionZones);
        
        // Add dry bush clusters
        this.addDryBushClusters(terrainSize, this.roadExclusionZones);
        
        // Add tumbleweeds
        this.addTumbleweeds(terrainSize, this.roadExclusionZones);
        
        // Add a few pyramids
        this.addPyramids(terrainSize, this.roadExclusionZones);
        
        return {
            trees: this.trees,
            rocks: this.rocks,
            cacti: this.cacti,
            pyramids: this.pyramids,
            dryBushes: this.dryBushes,
            tumbleweeds: this.tumbleweeds,
            obstacles: this.obstacles
        };
    }
    
    addTreeClusters(terrainSize, roadExclusionZones) {
        const treeClusters = 15;
        for (let c = 0; c < treeClusters; c++) {
            // Create a cluster center point
            const clusterCenter = this.getValidPosition(terrainSize, roadExclusionZones);
            const clusterRadius = 30 + Math.random() * 50;
            const treesInCluster = 5 + Math.floor(Math.random() * 7);
            
            // Choose a tree style for this cluster
            const treeStyle = Math.random() > 0.5 ? 'conifer' : 'broadleaf';
            
            for (let i = 0; i < treesInCluster; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * clusterRadius;
                const offsetX = Math.cos(angle) * distance;
                const offsetZ = Math.sin(angle) * distance;
                
                const position = {
                    x: clusterCenter.x + offsetX,
                    z: clusterCenter.z + offsetZ
                };
                
                this.addTree(position.x, position.z, treeStyle);
            }
        }
    }
    
    addIndividualTrees(terrainSize, roadExclusionZones) {
        for (let i = 0; i < 40; i++) {
            const position = this.getValidPosition(terrainSize, roadExclusionZones);
            const treeType = Math.random() > 0.4 ? 'conifer' : 'broadleaf';
            this.addTree(position.x, position.z, treeType);
        }
    }
    
    addCactiClusters(terrainSize, roadExclusionZones) {
        const cactiClusters = 10;
        for (let c = 0; c < cactiClusters; c++) {
            const clusterCenter = this.getValidPosition(terrainSize, roadExclusionZones);
            const clusterRadius = 15 + Math.random() * 25;
            const cactiInCluster = 3 + Math.floor(Math.random() * 4);
            
            // Choose a cactus style for this cluster
            const cactiStyles = ['saguaro', 'barrel', 'prickly'];
            const dominantStyle = cactiStyles[Math.floor(Math.random() * cactiStyles.length)];
            
            for (let i = 0; i < cactiInCluster; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * clusterRadius;
                const offsetX = Math.cos(angle) * distance;
                const offsetZ = Math.sin(angle) * distance;
                
                const position = {
                    x: clusterCenter.x + offsetX,
                    z: clusterCenter.z + offsetZ
                };
                
                // Use dominant style 70% of the time, random style otherwise
                const style = Math.random() > 0.3 ? dominantStyle : 'random';
                const sizeFactor = 0.8 + Math.random() * 0.5; // Varied sizes in clusters
                this.addCactus(position.x, position.z, sizeFactor, style);
            }
        }
    }
    
    addIndividualCacti(terrainSize, roadExclusionZones) {
        for (let i = 0; i < 25; i++) {
            const position = this.getValidPosition(terrainSize, roadExclusionZones);
            const sizeFactor = 0.9 + Math.random() * 0.6;
            this.addCactus(position.x, position.z, sizeFactor, 'random');
        }
    }
    
    addRockClusters(terrainSize, roadExclusionZones) {
        const rockClusters = 12;
        for (let c = 0; c < rockClusters; c++) {
            const clusterCenter = this.getValidPosition(terrainSize, roadExclusionZones);
            const clusterRadius = 10 + Math.random() * 20;
            const rocksInCluster = 3 + Math.floor(Math.random() * 5);
            
            for (let i = 0; i < rocksInCluster; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * clusterRadius;
                const offsetX = Math.cos(angle) * distance;
                const offsetZ = Math.sin(angle) * distance;
                
                const position = {
                    x: clusterCenter.x + offsetX,
                    z: clusterCenter.z + offsetZ
                };
                
                const sizeFactor = 0.8 + Math.random() * 0.5; // Varied sizes in clusters
                this.addRock(position.x, position.z, sizeFactor);
            }
        }
    }
    
    addIndividualRocks(terrainSize, roadExclusionZones) {
        for (let i = 0; i < 30; i++) {
            const position = this.getValidPosition(terrainSize, roadExclusionZones);
            const sizeFactor = 0.9 + Math.random() * 0.6;
            this.addRock(position.x, position.z, sizeFactor);
        }
    }
    
    addRockFormations(terrainSize, roadExclusionZones) {
        // Add 3-5 large rock formations (buttes/mesas) in strategic locations
        const formationCount = 10 + Math.floor(Math.random() * 3);
        
        for (let i = 0; i < formationCount; i++) {
            // Get a position far from other formations
            const position = this.getValidPosition(terrainSize, roadExclusionZones, 100);
            
            // Create the main formation
            this.addRockFormation(position.x, position.z);
            
            // Add smaller formations nearby to create a more natural grouping
            const smallerFormations = 1 + Math.floor(Math.random() * 2);
            for (let j = 0; j < smallerFormations; j++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = 30 + Math.random() * 20;
                const offsetX = Math.cos(angle) * distance;
                const offsetZ = Math.sin(angle) * distance;
                
                this.addRockFormation(
                    position.x + offsetX,
                    position.z + offsetZ
                );
            }
            
            // Add this area as an exclusion zone for other formations
            roadExclusionZones.push({
                position: position,
                width: 150,
                length: 150,
                rotation: 0
            });
        }
    }
    
    addPyramids(terrainSize, roadExclusionZones) {
        // Use settings from gameSettings.js for pyramid configuration
        const minPyramids = PYRAMIDS.MIN_COUNT;
        const maxPyramids = PYRAMIDS.MAX_COUNT;
        const numberOfPyramids = minPyramids + Math.floor(Math.random() * (maxPyramids - minPyramids + 1));
        
        //console.log(`Creating ${numberOfPyramids} pyramids in the environment`);
        
        // Create a list of exclusion zones for gas stations and bus starting area
        const pyramidExclusionZones = [...roadExclusionZones];
        
        // Add gas station exclusion zones if they exist in the scene
        if (this.scene.userData && this.scene.userData.gasStations) {
            const gasStations = this.scene.userData.gasStations;
            for (const station of gasStations) {
                pyramidExclusionZones.push({
                    position: { x: station.position.x, z: station.position.z },
                    width: PYRAMIDS.EXCLUSION_RADIUS * 2, // Use the exclusion radius from settings
                    length: PYRAMIDS.EXCLUSION_RADIUS * 2,
                    rotation: station.rotation || 0
                });
            }
        }
        
        // Add bus starting area exclusion zone (center of the map)
        pyramidExclusionZones.push({
            position: { x: 0, z: 0 },
            width: PYRAMIDS.EXCLUSION_RADIUS * 2, // Use the exclusion radius from settings
            length: PYRAMIDS.EXCLUSION_RADIUS * 2,
            rotation: 0
        });
        
        // Keep track of placed pyramids to maintain minimum distance between them
        const placedPyramidPositions = [];
        const minPyramidDistance = PYRAMIDS.MIN_DISTANCE;
        
        for (let i = 0; i < numberOfPyramids; i++) {
            let position;
            let validPosition = false;
            let attempts = 0;
            
            while (!validPosition && attempts < 50) {
                // Get a random position
                position = this.getValidPosition(terrainSize, pyramidExclusionZones);
                
                // Check distance from other pyramids
                validPosition = true;
                for (const existingPosition of placedPyramidPositions) {
                    const distance = Math.sqrt(
                        Math.pow(existingPosition.x - position.x, 2) +
                        Math.pow(existingPosition.z - position.z, 2)
                    );
                    
                    if (distance < minPyramidDistance) {
                        validPosition = false;
                        break;
                    }
                }
                
                attempts++;
            }
            
            if (validPosition) {
                // Store the position for future pyramid placement checks
                placedPyramidPositions.push(position);
                
                // Use settings for pyramid dimensions
                const widthRange = PYRAMIDS.MAX_WIDTH - PYRAMIDS.MIN_WIDTH;
                const heightRange = PYRAMIDS.MAX_HEIGHT - PYRAMIDS.MIN_HEIGHT;
                
                const width = PYRAMIDS.MIN_WIDTH + Math.random() * widthRange;
                const height = PYRAMIDS.MIN_HEIGHT + Math.random() * heightRange;
                
                // Clear a larger area around the pyramid to make it stand out
                this.clearArea(position.x, position.z, width * 2);
                
                // Add the pyramid
                this.addPyramid(position.x, position.z, width, height);
            }
        }
    }
    
    getValidPosition(terrainSize, exclusionZones) {
        const halfSize = terrainSize / 2;
        let isValid = false;
        let x, z;
        
        // Try to find a valid position outside of exclusion zones
        let attempts = 0;
        while (!isValid && attempts < 50) {
            attempts++;
            x = Math.random() * terrainSize - halfSize;
            z = Math.random() * terrainSize - halfSize;
            
            isValid = true;
            
            // Check against each exclusion zone
            for (const zone of exclusionZones) {
                // Transform coordinates to road's local space
                const relX = x - zone.position.x;
                const relZ = z - zone.position.z;
                
                // Rotate point to align with road orientation
                const rotatedX = relX * Math.cos(-zone.rotation) - relZ * Math.sin(-zone.rotation);
                const rotatedZ = relX * Math.sin(-zone.rotation) + relZ * Math.cos(-zone.rotation);
                
                // Check if point is within road boundaries
                if (
                    Math.abs(rotatedX) < zone.width / 2 &&
                    Math.abs(rotatedZ) < zone.length / 2
                ) {
                    isValid = false;
                    break;
                }
            }
        }
        
        return { x, z };
    }
    
    addTree(x, z, type = 'random') {
        // Determine tree type if random
        const treeType = type === 'random' ?
            (Math.random() > 0.5 ? 'conifer' : 'broadleaf') : type;
            
        // Generate random height for variation
        const height = 6 + Math.random() * 6;
        
        // Create tree with Tree class for physics
        const tree = new Tree(this.scene, this.physics, { x, z }, treeType);
        
        // Use instance mesh if available
        if (this.game && this.game.instanceMeshManager) {
            // Remove visual meshes since we're using instanced meshes
            if (tree.trunk) {
                this.scene.remove(tree.trunk);
            }
            
            if (tree.foliage && tree.foliage.length) {
                for (const foliage of tree.foliage) {
                    this.scene.remove(foliage);
                }
            }
            
            if (tree.treeGroup) {
                this.scene.remove(tree.treeGroup);
            }
            
            this.game.instanceMeshManager.addTree({ x, z }, treeType, height);
        }
        
        // Add to trees array for tracking
        this.trees.push(tree);
        
        return tree;
    }
    
    addRock(x, z, sizeFactor = 1, isLargeFormation = false) {
        // Create rock
        const rock = new Rock(this.scene, this.physics, { x, z }, sizeFactor, isLargeFormation);
        
        // Use instance mesh if available
        if (this.game && this.game.instanceMeshManager) {
            // Remove visual mesh since we're using instanced mesh
            if (rock.mesh) {
                this.scene.remove(rock.mesh);
            }
            
            this.game.instanceMeshManager.addRock({ x, z }, sizeFactor, isLargeFormation);
        }
        
        this.rocks.push(rock);
        return rock;
    }
    
    addRockFormation(x, z) {
        // Use the game's instance mesh manager for visuals
        const sizeFactor = 1; // Default size factor is 1
        const isLargeFormation = true;
        
        if (this.game && this.game.instanceMeshManager) {
            this.game.instanceMeshManager.addRock({ x, z }, sizeFactor, isLargeFormation);
        }
        
        return this.addRock(x, z, sizeFactor, isLargeFormation);
    }
    
    addCactus(x, z, sizeFactor = 1, style = 'random') {
        // Create cactus with Cactus class for physics
        const cactus = new Cactus(this.scene, this.physics, { x, z }, sizeFactor, style);
        
        // Use instance mesh if available
        if (this.game && this.game.instanceMeshManager) {
            // Remove visual meshes since we're using instanced meshes
            if (cactus.mesh) {
                this.scene.remove(cactus.mesh);
            }
            
            if (cactus.cactusGroup) {
                this.scene.remove(cactus.cactusGroup);
            }
            
            this.game.instanceMeshManager.addCactus({ x, z }, sizeFactor, style);
        }
        
        // Add to cacti array for tracking
        this.cacti.push(cactus);
        
        return cactus;
    }
    
    addPyramid(x, z, width = PYRAMIDS.MIN_WIDTH, height = PYRAMIDS.MIN_HEIGHT) {
        // Create position object
        const position = { x, y: 0, z };
        
        // Create pyramid with Pyramid class
        const pyramid = new Pyramid(this.scene, this.physics, position, { 
            width: width,
            height: height
        });
        
        // Add to pyramids array for tracking
        this.pyramids.push(pyramid);
        
        return pyramid;
    }
    
    removeTree(tree) {
        if (!tree) return;
        
        // Remove from scene
        if (tree.mesh) {
            this.scene.remove(tree.mesh);
        }
        
        // Remove from physics if applicable
        if (tree.collider && this.physics) {
            this.physics.removeCollider(tree.collider);
        }
        
        // Remove from trees array
        const index = this.trees.indexOf(tree);
        if (index !== -1) {
            this.trees.splice(index, 1);
        }
    }
    
    removeRock(rock) {
        if (!rock) return;
        
        // Remove from scene
        if (rock.mesh) {
            this.scene.remove(rock.mesh);
        }
        
        // Remove from physics if applicable
        if (rock.collider && this.physics) {
            this.physics.removeCollider(rock.collider);
        }
        
        // Remove from rocks array
        const index = this.rocks.indexOf(rock);
        if (index !== -1) {
            this.rocks.splice(index, 1);
        }
    }
    
    removeCactus(cactus) {
        if (!cactus) return;
        
        // Remove from scene
        if (cactus.mesh) {
            this.scene.remove(cactus.mesh);
        }
        
        // Remove from physics if applicable
        if (cactus.collider && this.physics) {
            this.physics.removeCollider(cactus.collider);
        }
        
        // Remove from cacti array
        const index = this.cacti.indexOf(cactus);
        if (index !== -1) {
            this.cacti.splice(index, 1);
        }
    }
    
    removePyramid(pyramid) {
        if (!pyramid) return;
        
        // Remove from scene and physics
        pyramid.remove();
        
        // Remove from pyramids array
        const index = this.pyramids.indexOf(pyramid);
        if (index !== -1) {
            this.pyramids.splice(index, 1);
        }
    }

    addDryBushClusters(terrainSize, roadExclusionZones) {
        const bushClusters = 20;
        for (let c = 0; c < bushClusters; c++) {
            const clusterCenter = this.getValidPosition(terrainSize, roadExclusionZones);
            const clusterRadius = 15 + Math.random() * 20;
            const bushesInCluster = 4 + Math.floor(Math.random() * 4);
            
            for (let i = 0; i < bushesInCluster; i++) {
                const angle = Math.random() * Math.PI * 2;
                const distance = Math.random() * clusterRadius;
                const offsetX = Math.cos(angle) * distance;
                const offsetZ = Math.sin(angle) * distance;
                
                const position = {
                    x: clusterCenter.x + offsetX,
                    z: clusterCenter.z + offsetZ
                };
                
                this.addDryBush(position.x, position.z);
            }
        }
    }

    addTumbleweeds(terrainSize, roadExclusionZones) {
        const numberOfTumbleweeds = 15;
        for (let i = 0; i < numberOfTumbleweeds; i++) {
            const position = this.getValidPosition(terrainSize, roadExclusionZones);
            this.addTumbleweed(position.x, position.z);
        }
    }

    addDryBush(x, z) {
        const position = { x, z };
        const dryBush = new DryBush(this.scene, this.physics, position);
        this.dryBushes.push(dryBush);
        return dryBush;
    }

    addTumbleweed(x, z) {
        const position = { x, z };
        const tumbleweed = new Tumbleweed(this.scene, this.physics, position);
        this.tumbleweeds.push(tumbleweed);
        return tumbleweed;
    }

    removeDryBush(dryBush) {
        if (!dryBush) return;
        
        if (dryBush.mesh) {
            this.scene.remove(dryBush.mesh);
        }
        
        if (dryBush.collider && this.physics) {
            this.physics.removeCollider(dryBush.collider);
        }
        
        const index = this.dryBushes.indexOf(dryBush);
        if (index !== -1) {
            this.dryBushes.splice(index, 1);
        }
    }

    removeTumbleweed(tumbleweed) {
        if (!tumbleweed) return;
        
        if (tumbleweed.mesh) {
            this.scene.remove(tumbleweed.mesh);
        }
        
        if (tumbleweed.collider && this.physics) {
            this.physics.removeCollider(tumbleweed.collider);
        }
        
        const index = this.tumbleweeds.indexOf(tumbleweed);
        if (index !== -1) {
            this.tumbleweeds.splice(index, 1);
        }
    }
    
    clearArea(centerX, centerZ, radius) {
        // Remove trees in the specified radius
        for (let i = this.trees.length - 1; i >= 0; i--) {
            const tree = this.trees[i];
            const dx = tree.position.x - centerX;
            const dz = tree.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removeTree(tree);
            }
        }
        
        // Remove rocks in the specified radius
        for (let i = this.rocks.length - 1; i >= 0; i--) {
            const rock = this.rocks[i];
            const dx = rock.position.x - centerX;
            const dz = rock.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removeRock(rock);
            }
        }
        
        // Remove cacti in the specified radius
        for (let i = this.cacti.length - 1; i >= 0; i--) {
            const cactus = this.cacti[i];
            const dx = cactus.position.x - centerX;
            const dz = cactus.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removeCactus(cactus);
            }
        }
        
        // Remove pyramids in the specified radius
        for (let i = this.pyramids.length - 1; i >= 0; i--) {
            const pyramid = this.pyramids[i];
            const dx = pyramid.position.x - centerX;
            const dz = pyramid.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removePyramid(pyramid);
            }
        }

        // Remove dry bushes in the specified radius
        for (let i = this.dryBushes.length - 1; i >= 0; i--) {
            const dryBush = this.dryBushes[i];
            const dx = dryBush.position.x - centerX;
            const dz = dryBush.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removeDryBush(dryBush);
            }
        }

        // Remove tumbleweeds in the specified radius
        for (let i = this.tumbleweeds.length - 1; i >= 0; i--) {
            const tumbleweed = this.tumbleweeds[i];
            const dx = tumbleweed.position.x - centerX;
            const dz = tumbleweed.position.z - centerZ;
            const distanceSquared = dx * dx + dz * dz;
            
            if (distanceSquared < radius * radius) {
                this.removeTumbleweed(tumbleweed);
            }
        }
    }
}